import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'

export interface TDRatings {
  A: number
  B: number
  C: number
  D: number
  F: number
  TOTAL: number
}

export type TDSectionStatus = 'pending' | 'running' | 'done' | 'failed' | 'unknown'

export type TDRegulation = 'mdr' | 'ivdr' | 'unknown'

export interface TDSubsectionSpec {
  key: string
  title: string
  annex_ref?: string
}

export interface TDExtractionSpec {
  key: string
  title: string
}

export interface TDSectionGroupSpec {
  groupKey: string
  groupLabel: string
  annex_ref: string
  subsections: TDSubsectionSpec[]
  extractions: TDExtractionSpec[]
}

const MDR_TD_SECTION_GROUPS: readonly TDSectionGroupSpec[] = [
  {
    groupKey: 's1', groupLabel: '§1 Device Description', annex_ref: 'Annex II §1',
    subsections: [
      { key: 'device_description_core', title: 'Device Description - Core' },
      { key: 'device_description_technical', title: 'Device Description - Technical' },
    ],
    extractions: [
      { key: 'device_description_core', title: 'Device Description - Core' },
      { key: 'device_description_technical', title: 'Device Description - Technical' },
    ],
  },
  {
    groupKey: 's2', groupLabel: '§2 Information Supplied', annex_ref: 'Annex II §2; Annex I §23',
    subsections: [
      { key: 'info_labelling', title: 'Labelling' },
      { key: 'info_ifu', title: 'Instructions for Use (IFU)' },
    ],
    extractions: [
      { key: 'info_labelling', title: 'Labelling' },
      { key: 'info_ifu', title: 'Instructions for Use (IFU)' },
    ],
  },
  {
    groupKey: 's3', groupLabel: '§3 Design & Manufacturing', annex_ref: 'Annex II §3',
    subsections: [
      { key: 'dm_design_process', title: 'Design & Manufacturing Process' },
      { key: 'dm_sites_suppliers', title: 'Sites & Suppliers' },
    ],
    extractions: [
      { key: 'dm_design_process', title: 'Design & Manufacturing Process' },
      { key: 'dm_sites_suppliers', title: 'Sites & Suppliers' },
    ],
  },
  {
    groupKey: 's4', groupLabel: '§4 GSPR Compliance', annex_ref: 'Annex II §4; Annex I',
    subsections: [
      { key: 'gspr_matrix', title: 'GSPR Compliance - Matrix' },
      { key: 'gspr_sections', title: 'GSPR Compliance - Sections' },
    ],
    extractions: [
      { key: 'gspr_compliance', title: 'GSPR Compliance' },
    ],
  },
  {
    groupKey: 's5', groupLabel: '§5 Benefit-Risk & Risk Management', annex_ref: 'Annex II §5; Annex I §3, §8',
    subsections: [
      { key: 'benefit_risk_plan', title: 'Benefit-Risk - Plan' },
      { key: 'benefit_risk_analysis', title: 'Benefit-Risk - Hazard Analysis' },
      { key: 'benefit_risk_conclusion', title: 'Benefit-Risk - Conclusion' },
    ],
    extractions: [
      { key: 'benefit_risk_management', title: 'Benefit-Risk & Risk Management' },
    ],
  },
  {
    groupKey: 's6', groupLabel: '§6 Preclinical Data', annex_ref: 'Annex II §6.1(a)',
    subsections: [
      { key: 'preclinical_data', title: 'Preclinical Data' },
    ],
    extractions: [
      { key: 'preclinical_data', title: 'Preclinical Data' },
    ],
  },
  {
    groupKey: 's7', groupLabel: '§7 Verification & Validation', annex_ref: 'Annex II §6.1(b); Annex I §10-§18',
    subsections: [
      { key: 'vv_biocompatibility', title: 'V&V - Biocompatibility' },
      { key: 'vv_software', title: 'V&V - Software & Cybersecurity' },
      { key: 'vv_packaging_stability', title: 'V&V - Packaging & Stability' },
      { key: 'vv_electrical_performance', title: 'V&V - Electrical & Performance' },
      { key: 'vv_usability', title: 'V&V - Usability' },
    ],
    extractions: [
      { key: 'vv_physical', title: 'V&V - Physical' },
      { key: 'vv_digital', title: 'V&V - Digital' },
    ],
  },
  {
    groupKey: 's8', groupLabel: '§8 Clinical Evaluation', annex_ref: 'Annex II §6.1(c); Annex XIV Part A',
    subsections: [
      { key: 'ce_plan_strategy', title: 'Clinical Evaluation - Plan & Strategy' },
      { key: 'ce_data_evidence', title: 'Clinical Evaluation - Data & Evidence' },
      { key: 'ce_report_conclusion', title: 'Clinical Evaluation - CER & Conclusions' },
    ],
    extractions: [
      { key: 'clinical_evaluation', title: 'Clinical Evaluation' },
    ],
  },
  {
    groupKey: 's9', groupLabel: '§9 PMCF', annex_ref: 'Annex II §6.1(d); Annex XIV Part B',
    subsections: [
      { key: 'pmcf', title: 'Post-Market Clinical Follow-up (PMCF)' },
    ],
    extractions: [
      { key: 'pmcf', title: 'Post-Market Clinical Follow-up (PMCF)' },
    ],
  },
  {
    groupKey: 's10', groupLabel: '§10 Conditional Subsections', annex_ref: 'Annex II §6.2',
    subsections: [
      { key: 'conditional_subsections', title: 'Additional Information (Specific Cases)' },
    ],
    extractions: [
      { key: 'conditional_subsections', title: 'Additional Information (Specific Cases)' },
    ],
  },
  {
    groupKey: 's11', groupLabel: '§11 PMS Plan', annex_ref: 'Annex II §A.7',
    subsections: [
      { key: 'pms_data_sources', title: 'PMS Plan - Data Sources' },
      { key: 'pms_analysis_actions', title: 'PMS Plan - Analysis & Actions' },
    ],
    extractions: [
      { key: 'pms_plan', title: 'PMS Plan' },
    ],
  },
  {
    groupKey: 's12', groupLabel: '§12 PSUR', annex_ref: 'Annex II §A.8',
    subsections: [
      { key: 'psur', title: 'Periodic Safety Update Report (PSUR)' },
    ],
    extractions: [
      { key: 'psur', title: 'Periodic Safety Update Report (PSUR)' },
    ],
  },
  {
    groupKey: 's13', groupLabel: '§13 Declaration of Conformity', annex_ref: 'Art. 19; Annex IV',
    subsections: [
      { key: 'declaration_of_conformity', title: 'Declaration of Conformity' },
    ],
    extractions: [
      { key: 'declaration_of_conformity', title: 'Declaration of Conformity' },
    ],
  },
  {
    groupKey: 's14', groupLabel: '§14 SSCP', annex_ref: 'Art. 32; Annex III §2',
    subsections: [
      { key: 'sscp', title: 'Summary of Safety and Clinical Performance (SSCP)' },
    ],
    extractions: [
      { key: 'sscp', title: 'Summary of Safety and Clinical Performance (SSCP)' },
    ],
  },
  // Continuity sections (MYAC-634)
  {
    groupKey: 'continuity_ce', groupLabel: 'Continuity - Clinical Evaluation', annex_ref: 'Annex XIV Part A; Art. 61(11)',
    subsections: [{ key: 'continuity_clinical_evaluation', title: 'CER Continuity' }],
    extractions: [{ key: 'continuity_clinical_evaluation', title: 'CER Continuity' }],
  },
  {
    groupKey: 'continuity_pmcf', groupLabel: 'Continuity - PMCF', annex_ref: 'Annex XIV Part B; Art. 61(11)',
    subsections: [{ key: 'continuity_pmcf', title: 'PMCF Continuity' }],
    extractions: [{ key: 'continuity_pmcf', title: 'PMCF Continuity' }],
  },
  {
    groupKey: 'continuity_pms', groupLabel: 'Continuity - PMS Plan', annex_ref: 'Annex III §1; Art. 83; Art. 88',
    subsections: [{ key: 'continuity_pms_plan', title: 'PMS Plan Continuity' }],
    extractions: [{ key: 'continuity_pms_plan', title: 'PMS Plan Continuity' }],
  },
  {
    groupKey: 'continuity_psur', groupLabel: 'Continuity - PSUR', annex_ref: 'Art. 85-86',
    subsections: [{ key: 'continuity_psur', title: 'PSUR Continuity' }],
    extractions: [{ key: 'continuity_psur', title: 'PSUR Continuity' }],
  },
  {
    groupKey: 'continuity_doc', groupLabel: 'Continuity - Declaration of Conformity', annex_ref: 'Art. 19; Art. 29',
    subsections: [{ key: 'continuity_declaration_of_conformity', title: 'DoC Continuity' }],
    extractions: [{ key: 'continuity_declaration_of_conformity', title: 'DoC Continuity' }],
  },
  {
    groupKey: 'continuity_sscp', groupLabel: 'Continuity - SSCP', annex_ref: 'Art. 32; Art. 61(11); Art. 83(3)(d)',
    subsections: [{ key: 'continuity_sscp', title: 'SSCP Continuity' }],
    extractions: [{ key: 'continuity_sscp', title: 'SSCP Continuity' }],
  },
] as const

// IVDR TD V2 catalog — mirrors core_models_services IVDR_TD_SECTION_GROUPS
// (16 user-facing groups / 26 subsections). Keys + titles + annex refs come
// from repos/agents/business_data/ivdr/td/ivdr_td_requirements.yaml (v2.1,
// MYAC-646). Hand-maintained mirror — keep in sync with CMS. For IVDR each
// subsection has its own extraction, so extractions === subsections.
const IVDR_TD_SECTION_GROUPS: readonly TDSectionGroupSpec[] = [
  { groupKey: 's1', groupLabel: '§1 Device Description', annex_ref: 'IVDR Annex II §1',
    subsections: [
      { key: 'device_description_core', title: 'Device Description - Identity, Purpose & Classification' },
      { key: 'device_description_technical', title: 'Device Description - Components, Instruments, Software, Variants & Prior Generations' },
    ],
    extractions: [
      { key: 'device_description_core', title: 'Device Description - Identity, Purpose & Classification' },
      { key: 'device_description_technical', title: 'Device Description - Components, Instruments, Software, Variants & Prior Generations' },
    ] },
  { groupKey: 's2', groupLabel: '§2 Information Supplied', annex_ref: 'IVDR Annex II §2; Annex I §20',
    subsections: [{ key: 'info_supplied', title: 'Information Supplied by the Manufacturer (Labels + IFU)' }],
    extractions: [{ key: 'info_supplied', title: 'Information Supplied by the Manufacturer (Labels + IFU)' }] },
  { groupKey: 's3', groupLabel: '§3 Design & Manufacturing', annex_ref: 'IVDR Annex II §3',
    subsections: [
      { key: 'dm_design', title: 'Design Information' },
      { key: 'dm_manufacturing', title: 'Manufacturing Information & Sites/Subcontractors' },
    ],
    extractions: [
      { key: 'dm_design', title: 'Design Information' },
      { key: 'dm_manufacturing', title: 'Manufacturing Information & Sites/Subcontractors' },
    ] },
  { groupKey: 's4', groupLabel: '§4 GSPR Compliance', annex_ref: 'IVDR Annex II §4; Annex I',
    subsections: [{ key: 'gspr_compliance', title: 'GSPR Compliance Matrix' }],
    extractions: [{ key: 'gspr_compliance', title: 'GSPR Compliance Matrix' }] },
  { groupKey: 's5', groupLabel: '§5 Benefit-Risk Management', annex_ref: 'IVDR Annex II §5; Annex I §1, §3, §8',
    subsections: [{ key: 'benefit_risk_management', title: 'Benefit-Risk Analysis & Risk Management' }],
    extractions: [{ key: 'benefit_risk_management', title: 'Benefit-Risk Analysis & Risk Management' }] },
  { groupKey: 's6', groupLabel: '§6.1 Analytical Performance', annex_ref: 'IVDR Annex II §6.1; Annex XIII Part A',
    subsections: [
      { key: 'analytical_performance_specimen', title: 'Analytical Performance - Specimen Handling' },
      { key: 'analytical_performance_characteristics', title: 'Analytical Performance - Characteristics' },
      { key: 'analytical_performance_report', title: 'Analytical Performance Report' },
    ],
    extractions: [
      { key: 'analytical_performance_specimen', title: 'Analytical Performance - Specimen Handling' },
      { key: 'analytical_performance_characteristics', title: 'Analytical Performance - Characteristics' },
      { key: 'analytical_performance_report', title: 'Analytical Performance Report' },
    ] },
  { groupKey: 's7', groupLabel: '§6.2 Clinical Performance & Performance Evaluation', annex_ref: 'IVDR Annex XIII Part A',
    subsections: [
      { key: 'clinical_performance_pe_plan', title: 'Performance Evaluation Plan' },
      { key: 'clinical_performance_scientific_validity', title: 'Performance Evaluation - Scientific Validity' },
      { key: 'clinical_performance_cpsp', title: 'Clinical Performance Study Plan (CPSP)' },
      { key: 'clinical_performance_cpsr', title: 'Clinical Performance Study Report (CPSR)' },
      { key: 'clinical_performance_evaluation_report', title: 'Performance Evaluation Report (PER)' },
    ],
    extractions: [
      { key: 'clinical_performance_pe_plan', title: 'Performance Evaluation Plan' },
      { key: 'clinical_performance_scientific_validity', title: 'Performance Evaluation - Scientific Validity' },
      { key: 'clinical_performance_cpsp', title: 'Clinical Performance Study Plan (CPSP)' },
      { key: 'clinical_performance_cpsr', title: 'Clinical Performance Study Report (CPSR)' },
      { key: 'clinical_performance_evaluation_report', title: 'Performance Evaluation Report (PER)' },
    ] },
  { groupKey: 's8', groupLabel: '§6.3 Stability', annex_ref: 'IVDR Annex II §6.3',
    subsections: [{ key: 'stability', title: 'Stability (Shelf-life, In-use, Shipping - excluding specimen stability)' }],
    extractions: [{ key: 'stability', title: 'Stability (Shelf-life, In-use, Shipping - excluding specimen stability)' }] },
  { groupKey: 's9', groupLabel: '§6.4 Software Verification & Validation', annex_ref: 'IVDR Annex II §6.4; Annex I §16-17',
    subsections: [{ key: 'software_vv', title: 'Software Verification & Validation' }],
    extractions: [{ key: 'software_vv', title: 'Software Verification & Validation' }] },
  { groupKey: 's10', groupLabel: '§6.5 Additional Specific Cases', annex_ref: 'IVDR Annex II §6.5',
    subsections: [{ key: 'additional_specific_cases', title: 'Additional Information Required in Specific Cases (§6.5)' }],
    extractions: [{ key: 'additional_specific_cases', title: 'Additional Information Required in Specific Cases (§6.5)' }] },
  { groupKey: 's11', groupLabel: '§Article 78-80 Post-Market Surveillance', annex_ref: 'IVDR Annex III; Art. 78-81',
    subsections: [
      { key: 'pms_plan', title: 'Post-Market Surveillance Plan' },
      { key: 'pms_psr', title: 'PMS Report (PSR - Class A/B)' },
      { key: 'pms_psur', title: 'Periodic Safety Update Report (PSUR - Class C/D)' },
    ],
    extractions: [
      { key: 'pms_plan', title: 'Post-Market Surveillance Plan' },
      { key: 'pms_psr', title: 'PMS Report (PSR - Class A/B)' },
      { key: 'pms_psur', title: 'Periodic Safety Update Report (PSUR - Class C/D)' },
    ] },
  { groupKey: 's12', groupLabel: '§Annex XIII Part B Post-Market Performance Follow-up', annex_ref: 'IVDR Annex XIII Part B',
    subsections: [{ key: 'pmpf', title: 'Post-Market Performance Follow-up (PMPF)' }],
    extractions: [{ key: 'pmpf', title: 'Post-Market Performance Follow-up (PMPF)' }] },
  { groupKey: 's13', groupLabel: '§Annex IV Declaration of Conformity', annex_ref: 'IVDR Annex IV; Art. 17',
    subsections: [{ key: 'declaration_of_conformity', title: 'EU Declaration of Conformity (DoC)' }],
    extractions: [{ key: 'declaration_of_conformity', title: 'EU Declaration of Conformity (DoC)' }] },
  { groupKey: 's14', groupLabel: '§Article 29 Summary of Safety and Performance', annex_ref: 'IVDR Art. 29',
    subsections: [{ key: 'ssp', title: 'Summary of Safety and Performance (SSP - Class C/D)' }],
    extractions: [{ key: 'ssp', title: 'Summary of Safety and Performance (SSP - Class C/D)' }] },
  { groupKey: 's15', groupLabel: '§Article 48 EURL Verification', annex_ref: 'IVDR Art. 48; Art. 100',
    subsections: [{ key: 'eurl_verification', title: 'EU Reference Laboratory Verification (Class D)' }],
    extractions: [{ key: 'eurl_verification', title: 'EU Reference Laboratory Verification (Class D)' }] },
  { groupKey: 's16', groupLabel: '§Annex IX §5.2 Companion Diagnostic', annex_ref: 'IVDR Art. 2(7); Annex IX §5.2',
    subsections: [{ key: 'companion_diagnostic', title: 'Companion Diagnostic - Specific Obligations' }],
    extractions: [{ key: 'companion_diagnostic', title: 'Companion Diagnostic - Specific Obligations' }] },
  // Continuity sections
  {
    groupKey: 'continuity_cp', groupLabel: 'Continuity - Clinical Performance', annex_ref: 'Art. 56(6); Annex XIII §1.3.3',
    subsections: [{ key: 'continuity_clinical_performance', title: 'Clinical Performance Evaluation Continuity' }],
    extractions: [{ key: 'continuity_clinical_performance', title: 'Clinical Performance Evaluation Continuity' }],
  },
  {
    groupKey: 'continuity_pmpf', groupLabel: 'Continuity - PMPF', annex_ref: 'Annex XIII Part B §5.2(h); §6–§8',
    subsections: [{ key: 'continuity_pmpf', title: 'PMPF Continuity' }],
    extractions: [{ key: 'continuity_pmpf', title: 'PMPF Continuity' }],
  },
  {
    groupKey: 'continuity_pms', groupLabel: 'Continuity - PMS Plan', annex_ref: 'Art. 78(3)(c); Annex III §1',
    subsections: [{ key: 'continuity_pms_plan', title: 'PMS Plan Continuity' }],
    extractions: [{ key: 'continuity_pms_plan', title: 'PMS Plan Continuity' }],
  },
  {
    groupKey: 'continuity_psur', groupLabel: 'Continuity - PSUR', annex_ref: 'Art. 81(1); Annex III §2',
    subsections: [{ key: 'continuity_psur', title: 'PSUR Continuity' }],
    extractions: [{ key: 'continuity_psur', title: 'PSUR Continuity' }],
  },
  {
    groupKey: 'continuity_psr', groupLabel: 'Continuity - PSR', annex_ref: 'Art. 80; Annex III §2',
    subsections: [{ key: 'continuity_psr', title: 'PSR Continuity' }],
    extractions: [{ key: 'continuity_psr', title: 'PSR Continuity' }],
  },
  {
    groupKey: 'continuity_doc', groupLabel: 'Continuity - Declaration of Conformity', annex_ref: 'Art. 17(1); Annex IV',
    subsections: [{ key: 'continuity_declaration_of_conformity', title: 'Declaration of Conformity Continuity' }],
    extractions: [{ key: 'continuity_declaration_of_conformity', title: 'Declaration of Conformity Continuity' }],
  },
  {
    groupKey: 'continuity_ssp', groupLabel: 'Continuity - SSP', annex_ref: 'Art. 29; Art. 56(6)',
    subsections: [{ key: 'continuity_ssp', title: 'SSP Continuity' }],
    extractions: [{ key: 'continuity_ssp', title: 'SSP Continuity' }],
  },
  {
    groupKey: 'continuity_eurl', groupLabel: 'Continuity - EURL Verification', annex_ref: 'Art. 48(13)(c); Art. 100',
    subsections: [{ key: 'continuity_eurl_verification', title: 'EURL Verification Continuity' }],
    extractions: [{ key: 'continuity_eurl_verification', title: 'EURL Verification Continuity' }],
  },
] as const

export function tdSectionGroups(regulation: TDRegulation): readonly TDSectionGroupSpec[] {
  return regulation === 'ivdr' ? IVDR_TD_SECTION_GROUPS : MDR_TD_SECTION_GROUPS
}

export function tdSubsectionByKey(
  regulation: TDRegulation,
  key: string,
): { group: TDSectionGroupSpec; subsection: TDSubsectionSpec } | undefined {
  for (const group of tdSectionGroups(regulation)) {
    const sub = group.subsections.find((s) => s.key === key)
    if (sub) return { group, subsection: sub }
  }
  return undefined
}

export function tdGroupForSubsection(regulation: TDRegulation, key: string): TDSectionGroupSpec | undefined {
  return tdSectionGroups(regulation).find((g) => g.subsections.some((s) => s.key === key))
}

export function tdAllSubsectionKeys(regulation: TDRegulation): string[] {
  return tdSectionGroups(regulation).flatMap((g) => g.subsections.map((s) => s.key))
}

export function tdExtractionKeysForGroup(group: TDSectionGroupSpec): string[] {
  return group.extractions.map((e) => e.key)
}

export function tdExtractionKeysForSubsection(regulation: TDRegulation, subsectionKey: string): string[] {
  const group = tdGroupForSubsection(regulation, subsectionKey)
  return group ? tdExtractionKeysForGroup(group) : [subsectionKey]
}

@Exclude()
export class TDSectionSummary {
  @Expose() section_title!: string
  @Expose() annex_ref?: string
  @Expose() status!: TDSectionStatus
  @Expose() overall_rating?: string
  @Expose() rating_counts?: TDRatings
  @Expose() proposed_tasks_counts?: { A: number; B: number; C: number; D: number; TOTAL: number }
  @Expose() @Transform(anyToDate) last_run_at?: Date
  @Expose() last_error?: string
  @Expose() latest_assessment_id?: string
}

export interface TDSource {
  file: string
  section_number?: string
  section_title?: string
  quotation?: string
  justification?: string
  document_path?: string
}

export type TDFieldCompleteness = 'present' | 'not_found' | 'not_applicable' | 'absence_justified'

export interface TDExtractedField {
  key: string
  value: string
  label: string
  ref?: string
  excerpt?: string
  sources: TDSource[]
  completeness?: TDFieldCompleteness | null
  completeness_justification?: string
}

export interface TDEvidence {
  file: string
  section_number?: string
  section_title?: string
  quotation?: string
  justification?: string
  document_path?: string
}

export class TDExtraction extends _FirebaseDocument {
  @Expose() section_key!: string
  @Expose() section_title!: string
  @Expose() annex_ref?: string
  @Expose() fields: TDExtractedField[] = []

  collection(): string { return 'extractions' }

  static fromFirebase(o: DocumentSnapshot): TDExtraction | null {
    return _FirebaseDocument._fromFirebase(TDExtraction, o)
  }
}

// MYAC-643 — TD-wide master applied-standards list (TDAppliedStandards).
export type AppliedStandardApplication =
  | 'full'
  | 'partial'
  | 'not_applicable'
  | 'unspecified'

export interface AppliedStandardSource {
  filename: string
  project_id?: string
  document_id?: string
  datasource_id?: string
}

export interface AppliedStandard {
  designation: string
  edition?: string | null
  application: AppliedStandardApplication
  application_justification?: string | null
  sources: AppliedStandardSource[]
  excerpt?: string | null
  regulation_watch_status?: string | null
}

export class TDAppliedStandards extends _FirebaseDocument {
  @Expose() standards: AppliedStandard[] = []
  @Expose() auxiliary_standards: AppliedStandard[] = []
  // reference_date / generated date of the regulatory monitoring report
  // ("veille") whose entries produced the regulation_watch_status badges. Null
  // when no veille report backed the labels. Stamped by the assessments
  // applied_standards job.
  @Expose() regulation_watch_reference_date: string | null = null
  @Expose() regulation_watch_generated_date: string | null = null

  collection(): string { return 'applied_standards' }

  static fromFirebase(o: DocumentSnapshot): TDAppliedStandards | null {
    return _FirebaseDocument._fromFirebase(TDAppliedStandards, o)
  }
}

// MYAC-593 — TD device "features" (the closed-vocabulary device-attributes
// payload). One master doc per product, regulation-aware: MDR carries
// risk_class/intended_user + 17 booleans; IVDR adds device_archetype +
// classification_rule with its own boolean set. The frontend duck-types the
// regulation from the payload shape (device_archetype ⇒ IVDR), matching the
// backend (JobTDDeviceAttributes) and web-admin renderer.
export type EvidenceStrength = 'direct' | 'inferred' | 'default'

// sources reuse ProjectDocumentSource, same as AppliedStandardSource.
export interface AttributeJustification {
  attribute: string
  value: string
  sources: AppliedStandardSource[]
  excerpt?: string | null
  rationale: string
  evidence_strength: EvidenceStrength
}

export class TDDeviceAttributes extends _FirebaseDocument {
  // Enums (shared MDR + IVDR). Raw enum string values, e.g. 'class_iia', 'lay'.
  @Expose() risk_class?: string
  @Expose() intended_user?: string
  // IVDR-only enum + free-text rule
  @Expose() device_archetype?: string
  @Expose() classification_rule?: string

  // MDR booleans
  @Expose() is_active?: boolean
  @Expose() is_implantable?: boolean
  @Expose() is_invasive?: boolean
  @Expose() is_software_only?: boolean
  @Expose() has_software?: boolean
  @Expose() is_single_use?: boolean
  @Expose() is_reusable_surgical?: boolean
  @Expose() is_custom_made?: boolean
  @Expose() is_system_or_procedure_pack?: boolean
  @Expose() contains_medicinal_substance?: boolean
  @Expose() contains_human_or_animal_tissues?: boolean
  @Expose() contains_cmr_substances?: boolean
  @Expose() contains_nanomaterials?: boolean
  @Expose() emits_radiation?: boolean

  // IVDR booleans
  @Expose() contains_biological_material?: boolean
  @Expose() is_companion_diagnostic?: boolean
  @Expose() has_prior_marketing_history?: boolean
  @Expose() device_processes_specimens?: boolean
  @Expose() has_data_interpretation_software?: boolean

  // Shared booleans
  @Expose() is_sterile?: boolean
  @Expose() is_networked?: boolean
  @Expose() has_measuring_function?: boolean

  // Market status
  @Expose() is_on_market?: boolean
  @Expose() market_placement_date?: string | null

  // Provenance / traceability
  @Expose() extraction_notes?: string
  @Expose() attribute_justifications: AttributeJustification[] = []

  collection(): string { return 'device_attributes' }

  get isIvdr(): boolean { return this.device_archetype != null }

  static fromFirebase(o: DocumentSnapshot): TDDeviceAttributes | null {
    return _FirebaseDocument._fromFirebase(TDDeviceAttributes, o)
  }
}

export class TDRequirement extends _FirebaseDocument {
  @Expose() section_key!: string
  @Expose() topic_name!: string
  @Expose() requirement_id!: string
  @Expose() title!: string
  @Expose() regulation_ref?: string
  @Expose() severity?: string
  @Expose() compliance_rating!: string
  @Expose() answer_text?: string
  @Expose() rating_justification?: string
  @Expose() evidences: TDEvidence[] = []

  collection(): string { return 'requirements' }

  static fromFirebase(o: DocumentSnapshot): TDRequirement | null {
    return _FirebaseDocument._fromFirebase(TDRequirement, o)
  }
}

